import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
// AL-519 item 6: this view renders the RAW payload, so it needs the mapper as
// well as the label map to reach the same vocabulary as every other renderer.
import { mapAssociatedPersonRole } from "../../services/cddRecordsApi";
import { roleLabel } from "../cdd-records/sections/shared/associatedPersonsConfig";
import { fetchC2Profile } from "../../services/configService";
import useCddRecordsStore from "../../stores/cddRecordsStore";
import LoadingScreen from "../LoadingScreen";
import "./CDDRecordDetail.css";
import {
  CDDRecordLocal,
  getRiskColor,
  getRiskLabel,
  getStatusColor,
  getStatusLabel,
} from "./cddRecordDetailConfig";
import PortalHeader from "./PortalHeader";

const CDDRecordDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [record, setRecord] = useState<CDDRecordLocal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<any>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // Read from Zustand store — no separate fetch needed
  const storeRecords = useCddRecordsStore((state) => state.allRecords);

  useEffect(() => {
    try {
      setLoading(true);
      const foundRecord = storeRecords.find((r) => r.id === id);

      if (foundRecord) {
        setRecord(foundRecord as any);
      } else if (storeRecords.length > 0) {
        setError("Record not found");
      }
      // If store is empty, keep loading state (data may still be loading)
    } catch (err) {
      setError("Failed to load record details");
    } finally {
      setLoading(false);
    }
  }, [id, storeRecords]);

  // Fetch full C2 profile
  useEffect(() => {
    if (!id) return;
    setProfileLoading(true);
    fetchC2Profile(id)
      .then((data) => setProfile(data))
      .catch(() => {})
      .finally(() => setProfileLoading(false));
  }, [id]);

  const handleLogout = () => {
    navigate("/login");
  };

  const handleBackClick = () => {
    navigate(-1); // Go back to previous page (preserves filters and state)
  };

  const handleDownloadCertificate = () => {
    alert("📥 Downloading CDD certificate...");
  };

  const handlePrint = () => {
    alert("🖨️ Opening print dialog...");
  };

  const handleEmail = () => {
    alert("📧 Opening email dialog...");
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (error || !record) {
    return (
      <div className="portal-container">
        <PortalHeader onLogout={handleLogout} />
        <div className="error-state">
          <h2>⚠️ {error || "Record not found"}</h2>
          <button
            className="btn-back"
            data-testid="cdd-detail-back-btn"
            onClick={handleBackClick}
          >
            ← Back to Records
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="portal-container">
      <PortalHeader onLogout={handleLogout} />

      <main className="detail-container">
        <div className="detail-header">
          <button
            className="btn-back"
            data-testid="cdd-detail-back-btn"
            onClick={handleBackClick}
          >
            ← Back to Records
          </button>
          <h1>CDD Record Details</h1>
          <div className="header-actions">
            <button
              className="btn-action"
              data-testid="download-cdd-certificate-btn"
              onClick={handleDownloadCertificate}
              title="Download Certificate"
            >
              📥 Certificate
            </button>
            <button
              className="btn-action"
              data-testid="print-cdd-record-btn"
              onClick={handlePrint}
              title="Print"
            >
              🖨️ Print
            </button>
            <button
              className="btn-action"
              data-testid="email-cdd-record-btn"
              onClick={handleEmail}
              title="Email"
            >
              📧 Email
            </button>
          </div>
        </div>

        <div className="detail-grid">
          {/* Left Column - Key Information */}
          <div className="detail-column">
            <div className="detail-card">
              <h2>Record Information</h2>
              <div className="info-group">
                <label>Record ID</label>
                <span className="info-value">{record.id}</span>
              </div>
              <div className="info-group">
                <label>Reference</label>
                <span className="info-value reference-value">
                  {record.reference}
                </span>
              </div>
              <div className="info-group">
                <label>Client Name</label>
                <span className="info-value">{record.clientName}</span>
              </div>
              <div className="info-group">
                <label>Customer</label>
                <span className="info-value">{record.customer}</span>
              </div>
              <div className="info-group">
                <label>Email</label>
                <span className="info-value email-value">{record.email}</span>
              </div>
            </div>

            <div className="detail-card">
              <h2>Registration Details</h2>
              <div className="info-group">
                <label>ABN</label>
                <span className="info-value abn-value">{record.abn}</span>
              </div>
              <div className="info-group">
                <label>Entity Type</label>
                <span className="info-value">{record.type}</span>
              </div>
              <div className="info-group">
                <label>Risk Level</label>
                <span
                  className="info-value"
                  style={{
                    color: getRiskColor(record.riskLevel),
                    fontWeight: "bold",
                  }}
                >
                  {getRiskLabel(record.riskLevel)}
                </span>
              </div>
            </div>
          </div>

          {/* Right Column - Status & Dates */}
          <div className="detail-column">
            <div className="detail-card status-card">
              <h2>Status Information</h2>
              <div className="status-section">
                <label>Current Status</label>
                <div
                  className="status-badge-large"
                  style={{ backgroundColor: getStatusColor(record.status) }}
                >
                  {getStatusLabel(record.status)}
                </div>
              </div>

              <div className="info-group">
                <label>Created Date</label>
                <span className="info-value">
                  {new Date(record.createdDate).toLocaleDateString("en-AU")}
                </span>
              </div>

              {record.completedDate && (
                <div className="info-group">
                  <label>Completed Date</label>
                  <span className="info-value">
                    {new Date(record.completedDate).toLocaleDateString("en-AU")}
                  </span>
                </div>
              )}

              {record.reason && (
                <div className="info-group reason-section">
                  <label>Reason for Status</label>
                  <span className="info-value reason-text">
                    {record.reason}
                  </span>
                </div>
              )}
            </div>

            <div className="detail-card">
              <h2>Documents</h2>
              <div className="documents-list">
                {record.documents && record.documents.length > 0 ? (
                  <ul>
                    {record.documents.map((doc, idx) => (
                      <li key={idx} className="document-item">
                        <span className="doc-icon">📄</span>
                        <button
                          className="doc-link"
                          onClick={() => alert(`Viewing: ${doc}`)}
                        >
                          {doc}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="no-documents">No documents available</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Individual / Entity Details from C2 Profile */}
        {profileLoading && (
          <div className="detail-card full-width">
            <p>Loading profile details...</p>
          </div>
        )}

        {profile && profile.persons && profile.persons.length > 0 && (
          <div className="detail-card full-width">
            <h2>Individual Details</h2>
            {profile.persons.map((person: any) => (
              <div key={person.personId} className="profile-person-section">
                <div className="detail-grid">
                  <div className="detail-column">
                    <div className="info-group">
                      <label>Name</label>
                      <span className="info-value">
                        {person.firstName} {person.lastName}
                      </span>
                    </div>
                    {person.dateOfBirth && (
                      <div className="info-group">
                        <label>Date of Birth</label>
                        <span className="info-value">{person.dateOfBirth}</span>
                      </div>
                    )}
                    {person.email && (
                      <div className="info-group">
                        <label>Email</label>
                        <span className="info-value email-value">
                          {person.email}
                        </span>
                      </div>
                    )}
                    {person.phone && (
                      <div className="info-group">
                        <label>Phone</label>
                        <span className="info-value">{person.phone}</span>
                      </div>
                    )}
                    {person.roles && person.roles.length > 0 && (
                      <div className="info-group">
                        <label>Role</label>
                        {/*
                          AL-519 item 6: this printed the raw `roleType` straight
                          from the payload, so the same person read "Director" in
                          Associated Persons and `PRINCIPAL` here, and a C2 User
                          NonPrincipal showed a bare `PRINCIPAL` that was not an
                          office at all.

                          Routed through the same mapper and the same label map as
                          every other renderer. Mapping also drops superseded
                          principal roles, which this view was previously showing
                          as current.
                        */}
                        <span className="info-value">
                          {person.roles
                            .map((r: any) => mapAssociatedPersonRole(r, profile.entityType))
                            .filter((id: string | null): id is string => id !== null)
                            .map((id: string) => roleLabel(id))
                            .join(", ")}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="detail-column">
                    {person.identityCheck && (
                      <div className="detail-card">
                        <h3>Identity Check (KYC)</h3>
                        <div className="info-group">
                          <label>Status</label>
                          <span className="info-value">
                            {person.identityCheck.kycStatus}
                          </span>
                        </div>
                        {person.identityCheck.provider && (
                          <div className="info-group">
                            <label>Provider</label>
                            <span className="info-value">
                              {person.identityCheck.provider === 'SimpleKYC' || person.identityCheck.provider === 'IDVerse' ? 'ARCaml Engine' : person.identityCheck.provider}
                            </span>
                          </div>
                        )}
                        {person.identityCheck.checkedAt && (
                          <div className="info-group">
                            <label>Checked At</label>
                            <span className="info-value">
                              {new Date(
                                person.identityCheck.checkedAt,
                              ).toLocaleString("en-AU")}
                            </span>
                          </div>
                        )}
                        {person.identityCheck.verifiedName && (
                          <div className="info-group">
                            <label>Verified Name</label>
                            <span className="info-value">
                              {[
                                person.identityCheck.verifiedName.firstName,
                                person.identityCheck.verifiedName.middleNames,
                                person.identityCheck.verifiedName.lastName,
                              ]
                                .filter(Boolean)
                                .join(" ")}
                            </span>
                          </div>
                        )}
                        {person.identityCheck.mismatches &&
                          person.identityCheck.mismatches.length > 0 && (
                            <div className="info-group">
                              <label>Observations</label>
                              <ul className="mismatch-list">
                                {person.identityCheck.mismatches.map(
                                  (m: any, i: number) => (
                                    <li
                                      key={i}
                                      className={
                                        m.blocking
                                          ? "mismatch-blocking"
                                          : "mismatch-info"
                                      }
                                    >
                                      {m.detail}
                                    </li>
                                  ),
                                )}
                              </ul>
                            </div>
                          )}
                      </div>
                    )}

                    {person.pepTfsScreening && (
                      <div className="detail-card">
                        <h3>PEP / TFS Screening</h3>
                        <div className="info-group">
                          <label>Status</label>
                          <span className="info-value">
                            {person.pepTfsScreening.status}
                          </span>
                        </div>
                        {person.pepTfsScreening.provider && (
                          <div className="info-group">
                            <label>Provider</label>
                            <span className="info-value">
                              {person.pepTfsScreening.provider === 'SimpleKYC' || person.pepTfsScreening.provider === 'IDVerse' ? 'ARCaml Engine' : person.pepTfsScreening.provider}
                            </span>
                          </div>
                        )}
                        {person.pepTfsScreening.checkedAt && (
                          <div className="info-group">
                            <label>Checked At</label>
                            <span className="info-value">
                              {new Date(
                                person.pepTfsScreening.checkedAt,
                              ).toLocaleString("en-AU")}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {person.flags && person.flags.length > 0 && (
                  <div className="info-group">
                    <label>Active Flags</label>
                    <div className="flags-list">
                      {person.flags.map((flag: string) => (
                        <span key={flag} className="flag-badge">
                          {flag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Checks Log */}
        {profile && profile.checksLog && profile.checksLog.length > 0 && (
          <div className="detail-card full-width">
            <h2>Checks Log</h2>
            <div className="table-wrapper">
              <table className="records-table">
                <thead>
                  <tr>
                    <th>Check Type</th>
                    <th>Provider</th>
                    <th>Status</th>
                    <th>Checked At</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {profile.checksLog.map((check: any, i: number) => (
                    <tr key={i}>
                      <td>{check.checkType}</td>
                      <td>{check.provider === 'SimpleKYC' || check.provider === 'IDVerse' ? 'ARCaml Engine' : check.provider}</td>
                      <td>{check.status}</td>
                      <td>
                        {check.checkedAt
                          ? new Date(check.checkedAt).toLocaleString("en-AU")
                          : "-"}
                      </td>
                      <td>{check.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Full Width Summary Section */}
        <div className="detail-card full-width summary-card">
          <h2>CDD Process Summary</h2>
          <div className="summary-timeline">
            <div
              className={`timeline-item ${record.status === "completed" || record.status === "failed" ? "completed" : ""}`}
            >
              <div className="timeline-marker">1</div>
              <div className="timeline-content">
                <h4>Created</h4>
                <p>
                  {new Date(record.createdDate).toLocaleDateString("en-AU")}
                </p>
              </div>
            </div>
            <div className="timeline-connector"></div>
            <div
              className={`timeline-item ${record.status === "completed" ? "completed" : record.status === "failed" ? "failed" : ""}`}
            >
              <div className="timeline-marker">2</div>
              <div className="timeline-content">
                <h4>Verification</h4>
                <p>
                  {record.status === "under-review"
                    ? "In Progress"
                    : record.status === "completed"
                      ? "Passed"
                      : record.status === "failed"
                        ? "Failed"
                        : "Pending"}
                </p>
              </div>
            </div>
            <div className="timeline-connector"></div>
            <div
              className={`timeline-item ${record.status === "completed" ? "completed" : ""}`}
            >
              <div className="timeline-marker">3</div>
              <div className="timeline-content">
                <h4>Completed</h4>
                <p>
                  {record.completedDate
                    ? new Date(record.completedDate).toLocaleDateString("en-AU")
                    : "Not completed"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CDDRecordDetail;
